import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/sessions/bmint';
const AI_API = 'https://api.botanix.in/api/sessions/ai/chat';

const C = {
  bg:'#080F1A', surface:'#0D1A2A', surface2:'#112235',
  border:'rgba(255,255,255,.07)', border2:'rgba(255,255,255,.12)',
  text:'#F0F6FF', text2:'#A8BFD0', text3:'#506070',
  gold:'#C9A84C', goldBg:'rgba(201,168,76,.12)',
  accent:'#3AAF58', accentBg:'rgba(58,175,88,.1)',
  red:'#EF4444', redBg:'rgba(239,68,68,.1)',
  amber:'#F59E0B', amberBg:'rgba(245,158,11,.1)',
  blue:'#3B82F6', blueBg:'rgba(59,130,246,.1)',
  purple:'#8B5CF6', purpleBg:'rgba(139,92,246,.1)',
  cyan:'#06B6D4', cyanBg:'rgba(6,182,212,.1)',
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const DEMAND_COLOR = score =>
  score>=80?'#EF4444':score>=65?'#F59E0B':score>=45?'#3B82F6':score>=25?'#10B981':'#6B7280';

const DEMAND_LABEL = score =>
  score>=80?'PEAK':score>=65?'HIGH':score>=45?'NORMAL':score>=25?'LOW':'QUIET';

const RELEVANCE_COLOR = { High:'#EF4444', Medium:'#F59E0B', Low:'#10B981' };

const IMPACT_COLOR = {
  'Very High':'#EF4444','High':'#F59E0B',
  'Medium':'#3B82F6','Low':'#10B981',
};

export default function BMint() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pulse');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [competitorAnalysis, setCompetitorAnalysis] = useState(null);
  const [analyzingComp, setAnalyzingComp] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [campaignBrief, setCampaignBrief] = useState(null);
  const [generatingCampaign, setGeneratingCampaign] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [showAddComp, setShowAddComp] = useState(false);
  const [newComp, setNewComp] = useState({name:'',type:'Primary',location:'',distance_km:'',website:'',segments:'',notes:''});
  const [msg, setMsg] = useState('');
  const [venues, setVenues] = useState([]);
  const [eventIntel, setEventIntel] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [loadingLive, setLoadingLive] = useState(false);
  const [weather, setWeather] = useState(null);
  const [news, setNews] = useState(null);
  const [nearbyResorts, setNearbyResorts] = useState([]);

  const showMsg = m => { setMsg(m); setTimeout(()=>setMsg(''),4000); };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, compRes] = await Promise.all([
        fetch(`${API}/dashboard`).then(r=>r.json()),
        fetch(`${API}/competitors`).then(r=>r.json()),
      ]);
      setDashboard(dashRes);
      setCompetitors(Array.isArray(compRes)?compRes:[]);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const loadEventIntelligence = async () => {
    setLoadingEvents(true);
    try {
      // First get real venues from Google
      const venueRes = await fetch(`${API}/ncr-venues`);
      const venueData = await venueRes.json();
      setVenues(venueData.venues||[]);
      // Then run AI event intelligence
      const intelRes = await fetch(`${API}/event-intelligence`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ venues: venueData.venues, months: 'August-October 2026' })
      });
      const intel = await intelRes.json();
      setEventIntel(intel);
      showMsg(`✅ Found ${intel.events?.length||0} NCR events with marketing opportunities!`);
    } catch(e) { showMsg('Error loading event intelligence'); }
    setLoadingEvents(false);
  };

  const loadLiveData = async () => {
    setLoadingLive(true);
    try {
      const [weatherRes, newsRes, resortRes] = await Promise.all([
        fetch(`${API}/weather`).then(r=>r.json()),
        fetch(`${API}/news`).then(r=>r.json()),
        fetch(`${API}/competitor-places`).then(r=>r.json()),
      ]);
      setWeather(weatherRes);
      setNews(newsRes);
      setNearbyResorts(resortRes.places||[]);
      // Run AI digest
      const digestRes = await fetch(`${API}/ai-market-digest`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ weather:weatherRes, news:newsRes, competitors:resortRes })
      });
      const digest = await digestRes.json();
      setLiveData(digest);
      showMsg('✅ Live market data loaded!');
    } catch(e) { showMsg('Error loading live data'); }
    setLoadingLive(false);
  };

  const runMarketScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const r = await fetch(`${API}/scan-market`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ days:90 })
      });
      const d = await r.json();
      if (r.ok) { setScanResult(d); loadDashboard(); showMsg(`✅ Market scan complete! ${d.events?.length||0} events found.`); }
      else showMsg('Scan failed: '+d.error);
    } catch(e) { showMsg('Error'); }
    setScanning(false);
  };

  const runCompetitorAnalysis = async () => {
    if (!competitors.length) { showMsg('Add competitors first'); return; }
    setAnalyzingComp(true);
    try {
      const r = await fetch(`${API}/competitor-analysis`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ competitors })
      });
      const d = await r.json();
      if (r.ok) { setCompetitorAnalysis(d); setActiveTab('competitor'); }
      else showMsg('Analysis failed');
    } catch(e) { showMsg('Error'); }
    setAnalyzingComp(false);
  };

  const generateCampaign = async (opportunity) => {
    setSelectedOpportunity(opportunity);
    setGeneratingCampaign(true);
    setCampaignBrief(null);
    try {
      const r = await fetch(`${API}/generate-campaign`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ opportunity, context: { property:'Botanix Nature Resort', location:'Sohna Road Gurgaon' } })
      });
      const d = await r.json();
      if (r.ok) { setCampaignBrief(d); setActiveTab('campaigns'); }
      else showMsg('Campaign generation failed');
    } catch(e) { showMsg('Error'); }
    setGeneratingCampaign(false);
  };

  const addCompetitor = async () => {
    if (!newComp.name) return;
    try {
      const r = await fetch(`${API}/competitors`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify(newComp)
      });
      if (r.ok) { setShowAddComp(false); setNewComp({name:'',type:'Primary',location:'',distance_km:'',website:'',segments:'',notes:''}); loadDashboard(); showMsg('Competitor added!'); }
    } catch(e) {}
  };

  const askBMint = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(p=>[...p,{role:'user',content:msg}]);
    setChatLoading(true);
    try {
      const r = await fetch(AI_API, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          system: `You are B-MINT — Botanix Market Intelligence AI. You are an expert in hospitality market intelligence, competitor analysis, demand forecasting, and revenue strategy for Botanix Nature Resort (Sohna Road, Gurgaon). You have deep knowledge of NCR/Gurgaon hospitality market, Indian wedding/MICE/leisure travel patterns, and competitive dynamics. Current market data: ${JSON.stringify({calendar:dashboard?.calendar?.slice(0,5), events:dashboard?.events?.slice(0,3), competitors:competitors.slice(0,3)})}`,
          messages: [...chatMessages.slice(-6), {role:'user',content:msg}],
        })
      });
      const d = await r.json();
      setChatMessages(p=>[...p,{role:'assistant',content:d.content?.[0]?.text||'No response'}]);
    } catch(e) { setChatMessages(p=>[...p,{role:'assistant',content:'Error'}]); }
    setChatLoading(false);
  };

  // Build 90-day demand calendar combining calendar events + demand scores + booking pace
  const buildCalendar = () => {
    const days = [];
    const today = new Date();
    for (let i=0; i<90; i++) {
      const d = new Date(today);
      d.setDate(today.getDate()+i);
      const dateStr = d.toISOString().slice(0,10);
      const calEvent = (dashboard?.calendar||[]).find(c=>c.event_date?.slice(0,10)===dateStr);
      const demandScore = (dashboard?.demand_scores||[]).find(s=>s.score_date?.slice(0,10)===dateStr);
      const bookingPace = (dashboard?.booking_pace||[]).find(b=>b.date?.slice(0,10)===dateStr);
      const baseScore = calEvent ? parseInt(calEvent.demand_boost_pct||0)+40 : 40;
      const score = demandScore?.demand_score || baseScore;
      days.push({ date:dateStr, day:d, score, calEvent, demandScore, bookingPace });
    }
    return days;
  };

  const calendarDays = buildCalendar();
  const highDemandDays = calendarDays.filter(d=>d.score>=65).length;
  const lowDemandDays = calendarDays.filter(d=>d.score<40).length;

  const inp = {width:'100%',padding:'8px 12px',background:'#112235',border:'1px solid rgba(255,255,255,.1)',borderRadius:8,color:'#F0F6FF',fontSize:13,boxSizing:'border-box'};

  if (loading) return <div style={{background:C.bg,minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:C.text3}}>Loading B-MINT...</div>;

  return (
    <div style={{background:C.bg,minHeight:'100vh',color:C.text,fontFamily:'system-ui,sans-serif'}}>
      {/* Header */}
      <div style={{background:`linear-gradient(135deg,#0D1A2A 0%,#1A2E4A 100%)`,padding:'20px 28px',borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{width:48,height:48,borderRadius:12,background:`linear-gradient(135deg,${C.gold},#E8C97A)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:24}}>🌐</div>
            <div>
              <div style={{fontSize:22,fontWeight:900,color:'#fff',letterSpacing:'.02em'}}>B-MINT</div>
              <div style={{fontSize:12,color:C.text3}}>Botanix Market Intelligence · Gurgaon NCR · 90-day horizon</div>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={loadDashboard} style={{padding:'8px 14px',background:'rgba(255,255,255,.05)',border:`1px solid ${C.border2}`,borderRadius:8,color:C.text2,fontSize:12,cursor:'pointer'}}>↻</button>
            <button onClick={runCompetitorAnalysis} disabled={analyzingComp}
              style={{padding:'9px 14px',background:C.purpleBg,color:C.purple,border:`1px solid ${C.purple}44`,borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',opacity:analyzingComp?.6:1}}>
              {analyzingComp?'⏳':'🔍'} Competitor Watch
            </button>
            <button onClick={loadLiveData} disabled={loadingLive}
              style={{padding:'9px 16px',background:loadingLive?C.surface2:C.accentBg,color:loadingLive?C.text3:C.accent,border:`1px solid ${C.accent}44`,borderRadius:8,fontSize:13,fontWeight:700,cursor:loadingLive?'default':'pointer'}}>
              {loadingLive?'⏳ Loading...':'📡 Live Data'}
            </button>
            <button onClick={runMarketScan} disabled={scanning}
              style={{padding:'9px 20px',background:scanning?C.surface2:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:scanning?C.text3:'#1A1A1A',border:'none',borderRadius:8,fontSize:13,fontWeight:800,cursor:scanning?'default':'pointer'}}>
              {scanning?'⏳ Scanning...':'🛰️ Scan Market'}
            </button>
          </div>
        </div>

        {/* Quick stats */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10}}>
          {[
            {label:'High Demand Days',value:highDemandDays,color:C.red,icon:'🔥'},
            {label:'Low Demand Days',value:lowDemandDays,color:C.accent,icon:'📢'},
            {label:'Events Detected',value:(dashboard?.events||[]).length,color:C.blue,icon:'📅'},
            {label:'Opportunities',value:(dashboard?.opportunities||[]).length,color:C.gold,icon:'💡'},
            {label:'Competitors Tracked',value:competitors.length,color:C.purple,icon:'👁️'},
          ].map((s,i)=>(
            <div key={i} style={{background:'rgba(255,255,255,.04)',borderRadius:10,padding:'10px 14px',border:`1px solid ${C.border}`}}>
              <div style={{fontSize:20,marginBottom:4}}>{s.icon}</div>
              <div style={{fontSize:20,fontWeight:900,color:s.color}}>{s.value}</div>
              <div style={{fontSize:10,color:C.text3,textTransform:'uppercase'}}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{padding:'20px 28px'}}>
        {msg&&<div style={{padding:'10px 16px',borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600,background:C.accentBg,color:C.accent}}>{msg}</div>}

        {/* Tabs */}
        <div style={{display:'flex',gap:6,marginBottom:20,flexWrap:'wrap'}}>
          {[
            ['pulse','🌐 Market Pulse'],
            ['calendar','📅 90-Day Calendar'],
            ['events','📍 Event Radar'],
            ['competitor','🔍 Competitor Watch'],
            ['opportunities','💡 Opportunities'],
            ['campaigns','📣 Campaign AI'],
            ['ncrevents','🎪 NCR Events'],
            ['live','📡 Live Intelligence'],
            ['ask','💬 Ask B-MINT'],
          ].map(([id,label])=>(
            <button key={id} onClick={()=>setActiveTab(id)}
              style={{padding:'7px 14px',background:activeTab===id?C.gold:'transparent',color:activeTab===id?'#1A1A1A':C.text2,
                border:`1px solid ${activeTab===id?C.gold:C.border}`,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
              {label}
            </button>
          ))}
        </div>

        {/* MARKET PULSE */}
        {activeTab==='pulse' && (
          <div>
            {/* Scan result summary */}
            {scanResult && (
              <div style={{background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:14,padding:20,marginBottom:20}}>
                <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:12}}>🛰️ Latest Market Scan</div>
                <div style={{fontSize:13,color:C.text2,marginBottom:14,lineHeight:1.7}}>{scanResult.market_summary}</div>
                {scanResult.season_insights && (
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    {[
                      ['🌸 Peak Months', scanResult.season_insights.peak_months?.join(', ')],
                      ['📅 Need Months', scanResult.season_insights.need_months?.join(', ')],
                      ['💍 Wedding Season', scanResult.season_insights.wedding_season],
                      ['🏢 Corporate Season', scanResult.season_insights.corporate_season],
                    ].map(([label,value])=>value?(
                      <div key={label} style={{padding:'10px 14px',background:C.surface2,borderRadius:8}}>
                        <div style={{fontSize:11,color:C.text3,marginBottom:4}}>{label}</div>
                        <div style={{fontSize:12,color:C.text2}}>{value}</div>
                      </div>
                    ):null)}
                  </div>
                )}
              </div>
            )}

            {/* Top opportunities */}
            <div style={{marginBottom:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:12}}>💡 AI Opportunity Inbox</div>
              {(dashboard?.opportunities||[]).length===0 ? (
                <div style={{textAlign:'center',padding:40,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:32,marginBottom:10}}>🛰️</div>
                  <div style={{fontSize:14,color:C.text2,marginBottom:8}}>No opportunities detected yet</div>
                  <button onClick={runMarketScan} style={{padding:'10px 20px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                    Scan Market Now
                  </button>
                </div>
              ) : (
                <div style={{display:'grid',gap:10}}>
                  {(dashboard?.opportunities||[]).map((opp,i)=>(
                    <div key={i} style={{background:C.surface,border:`1px solid ${opp.priority==='High'?C.red:opp.priority==='Medium'?C.amber:C.accent}44`,borderRadius:12,padding:18,borderLeft:`4px solid ${opp.priority==='High'?C.red:opp.priority==='Medium'?C.amber:C.accent}`}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                        <div>
                          <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:4}}>
                            <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:opp.priority==='High'?C.redBg:opp.priority==='Medium'?C.amberBg:C.accentBg,color:opp.priority==='High'?C.red:opp.priority==='Medium'?C.amber:C.accent}}>{opp.priority}</span>
                            <span style={{fontSize:11,color:C.text3}}>{opp.opportunity_type}</span>
                          </div>
                          <div style={{fontSize:15,fontWeight:800,color:C.text}}>{opp.title}</div>
                        </div>
                        <div style={{textAlign:'right',flexShrink:0,marginLeft:12}}>
                          <div style={{fontSize:18,fontWeight:900,color:C.gold}}>{inr(opp.revenue_opportunity)}</div>
                          <div style={{fontSize:10,color:C.text3}}>Revenue Opportunity</div>
                        </div>
                      </div>
                      <div style={{fontSize:12,color:C.text2,marginBottom:10,lineHeight:1.6}}>{opp.description}</div>
                      <div style={{fontSize:12,color:C.gold,marginBottom:10}}>💡 {opp.recommended_action}</div>
                      <div style={{display:'flex',gap:8}}>
                        <button onClick={()=>generateCampaign(opp)} disabled={generatingCampaign}
                          style={{padding:'6px 14px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',opacity:generatingCampaign?.6:1}}>
                          {generatingCampaign?'⏳':'📣'} Generate Campaign
                        </button>
                        <button onClick={()=>setActiveTab('campaigns')}
                          style={{padding:'6px 12px',background:C.surface2,color:C.text2,border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,cursor:'pointer'}}>
                          View Campaigns
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming high impact calendar events */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.amber,marginBottom:14}}>📅 Upcoming High Impact Dates</div>
              {(dashboard?.calendar||[]).filter(e=>e.impact_level==='High'||e.impact_level==='Very High').slice(0,6).map((event,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.surface2,borderRadius:8,marginBottom:6}}>
                  <div style={{display:'flex',gap:12,alignItems:'center'}}>
                    <div style={{width:40,textAlign:'center'}}>
                      <div style={{fontSize:16,fontWeight:900,color:IMPACT_COLOR[event.impact_level]||C.amber}}>{new Date(event.event_date).getDate()}</div>
                      <div style={{fontSize:9,color:C.text3}}>{new Date(event.event_date).toLocaleString('default',{month:'short'})}</div>
                    </div>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:C.text}}>{event.event_name}</div>
                      <div style={{fontSize:11,color:C.text3}}>{event.event_type}</div>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:IMPACT_COLOR[event.impact_level]+'22',color:IMPACT_COLOR[event.impact_level]||C.amber}}>{event.impact_level}</span>
                    <span style={{fontSize:12,fontWeight:700,color:C.accent}}>+{event.demand_boost_pct}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 90-DAY CALENDAR */}
        {activeTab==='calendar' && (
          <div>
            <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:16}}>90-Day Market Opportunity Calendar</div>
            <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
              {[['🔴 PEAK','≥80',C.red],['🟠 HIGH','65-79',C.amber],['🔵 NORMAL','45-64',C.blue],['🟢 LOW','25-44',C.accent],['⚪ QUIET','<25',C.text3]].map(([label,range,color])=>(
                <div key={label} style={{padding:'4px 12px',background:`${color}22`,border:`1px solid ${color}44`,borderRadius:6,fontSize:11,color}}>
                  {label} ({range})
                </div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d=>(
                <div key={d} style={{textAlign:'center',padding:'6px',fontSize:10,color:C.text3,fontWeight:700}}>{d}</div>
              ))}
              {calendarDays.map((day,i)=>{
                const color = DEMAND_COLOR(day.score);
                const label = DEMAND_LABEL(day.score);
                const hasEvent = day.calEvent || day.demandScore;
                const hasBooking = day.bookingPace;
                return (
                  <div key={i} style={{background:C.surface,border:`1px solid ${hasEvent?color+'44':C.border}`,borderRadius:8,padding:'6px 4px',textAlign:'center',cursor:'pointer',minHeight:60,position:'relative'}}>
                    <div style={{fontSize:12,fontWeight:700,color}}>{day.day.getDate()}</div>
                    <div style={{fontSize:8,color:C.text3}}>{day.day.toLocaleString('default',{month:'short'})}</div>
                    <div style={{fontSize:9,fontWeight:700,color,marginTop:2}}>{label}</div>
                    {day.calEvent && <div style={{fontSize:7,color:C.amber,marginTop:1,overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>{day.calEvent.event_name.slice(0,10)}</div>}
                    {hasBooking && <div style={{position:'absolute',top:2,right:2,width:6,height:6,borderRadius:'50%',background:C.accent}} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* EVENT RADAR */}
        {activeTab==='events' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text}}>Event Radar — NCR/Gurgaon ({(dashboard?.events||[]).length} events)</div>
              <button onClick={runMarketScan} disabled={scanning}
                style={{padding:'8px 16px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',opacity:scanning?.6:1}}>
                {scanning?'⏳ Scanning...':'🛰️ Refresh Events'}
              </button>
            </div>
            {(dashboard?.events||[]).length===0 ? (
              <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:40,marginBottom:12}}>🛰️</div>
                <div style={{fontSize:14,color:C.text2,marginBottom:16}}>No events detected yet. Run Market Scan to discover upcoming events.</div>
                <button onClick={runMarketScan} style={{padding:'10px 24px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                  Scan Market Now
                </button>
              </div>
            ) : (
              <div style={{display:'grid',gap:12}}>
                {(dashboard?.events||[]).sort((a,b)=>b.event_impact_score-a.event_impact_score).map((event,i)=>(
                  <div key={i} style={{background:C.surface,border:`1px solid ${RELEVANCE_COLOR[event.botanix_relevance]||C.amber}44`,borderRadius:12,padding:18,borderLeft:`4px solid ${RELEVANCE_COLOR[event.botanix_relevance]||C.amber}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                      <div style={{flex:1}}>
                        <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:6}}>
                          <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:RELEVANCE_COLOR[event.botanix_relevance]+'22',color:RELEVANCE_COLOR[event.botanix_relevance]||C.amber}}>{event.botanix_relevance} Relevance</span>
                          <span style={{fontSize:11,color:C.text3}}>{event.category}</span>
                          {event.zone&&<span style={{fontSize:11,color:C.text3}}>Zone {event.zone}</span>}
                        </div>
                        <div style={{fontSize:16,fontWeight:800,color:C.text}}>{event.event_name}</div>
                        <div style={{fontSize:12,color:C.text3,marginTop:2}}>
                          📍 {event.location} · {event.distance_km}km away · 
                          {event.start_date&&` ${new Date(event.start_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}`}
                          {event.end_date&&event.end_date!==event.start_date&&` - ${new Date(event.end_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}`}
                        </div>
                      </div>
                      <div style={{textAlign:'right',flexShrink:0,marginLeft:12}}>
                        <div style={{fontSize:24,fontWeight:900,color:DEMAND_COLOR(event.event_impact_score)}}>{event.event_impact_score}</div>
                        <div style={{fontSize:10,color:C.text3}}>Impact Score</div>
                        {event.revenue_opportunity>0&&<div style={{fontSize:13,fontWeight:700,color:C.gold,marginTop:4}}>{inr(event.revenue_opportunity)}</div>}
                      </div>
                    </div>
                    {event.audience_type&&<div style={{fontSize:11,color:C.text2,marginBottom:6}}>👥 Audience: {event.audience_type}</div>}
                    {event.likely_demand_segments&&<div style={{fontSize:11,color:C.blue,marginBottom:8}}>📊 Demand: {event.likely_demand_segments}</div>}
                    {event.recommended_action&&<div style={{fontSize:12,color:C.gold,padding:'8px 12px',background:C.goldBg,borderRadius:6}}>💡 {event.recommended_action}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* COMPETITOR WATCH */}
        {activeTab==='competitor' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text}}>Competitor Intelligence</div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>setShowAddComp(true)} style={{padding:'8px 14px',background:C.accentBg,color:C.accent,border:`1px solid ${C.accent}44`,borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>+ Add Competitor</button>
                <button onClick={runCompetitorAnalysis} disabled={analyzingComp||!competitors.length}
                  style={{padding:'8px 14px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',opacity:analyzingComp||!competitors.length?.5:1}}>
                  {analyzingComp?'⏳ Analyzing...':'🔍 Run Analysis'}
                </button>
              </div>
            </div>

            {/* Add competitor form */}
            {showAddComp && (
              <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,marginBottom:16}}>
                <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:12}}>Add Competitor</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:10}}>
                  <div><div style={{fontSize:11,color:C.text3,marginBottom:4}}>Name *</div><input style={inp} value={newComp.name} onChange={e=>setNewComp(p=>({...p,name:e.target.value}))} placeholder="Resort name" /></div>
                  <div><div style={{fontSize:11,color:C.text3,marginBottom:4}}>Type</div><select style={inp} value={newComp.type} onChange={e=>setNewComp(p=>({...p,type:e.target.value}))}><option>Primary</option><option>Secondary</option><option>Wedding</option><option>MICE</option></select></div>
                  <div><div style={{fontSize:11,color:C.text3,marginBottom:4}}>Location</div><input style={inp} value={newComp.location} onChange={e=>setNewComp(p=>({...p,location:e.target.value}))} placeholder="City/Area" /></div>
                  <div><div style={{fontSize:11,color:C.text3,marginBottom:4}}>Distance (km)</div><input style={inp} type="number" value={newComp.distance_km} onChange={e=>setNewComp(p=>({...p,distance_km:e.target.value}))} /></div>
                  <div><div style={{fontSize:11,color:C.text3,marginBottom:4}}>Website</div><input style={inp} value={newComp.website} onChange={e=>setNewComp(p=>({...p,website:e.target.value}))} placeholder="website.com" /></div>
                  <div><div style={{fontSize:11,color:C.text3,marginBottom:4}}>Segments</div><input style={inp} value={newComp.segments} onChange={e=>setNewComp(p=>({...p,segments:e.target.value}))} placeholder="Wedding, Corporate" /></div>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={addCompetitor} style={{padding:'8px 20px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>Save</button>
                  <button onClick={()=>setShowAddComp(false)} style={{padding:'8px 16px',background:'transparent',color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:'pointer'}}>Cancel</button>
                </div>
              </div>
            )}

            {/* Competitor list */}
            {competitors.length===0 ? (
              <div style={{textAlign:'center',padding:40,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:32,marginBottom:10}}>👁️</div>
                <div style={{fontSize:14,color:C.text2,marginBottom:8}}>No competitors added yet</div>
                <div style={{fontSize:12,color:C.text3}}>Add your competitive set to enable AI analysis</div>
              </div>
            ) : (
              <div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(250px,1fr))',gap:10,marginBottom:20}}>
                  {competitors.map((comp,i)=>(
                    <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                        <div style={{fontSize:14,fontWeight:800,color:C.text}}>{comp.name}</div>
                        <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:C.purpleBg,color:C.purple}}>{comp.type}</span>
                      </div>
                      <div style={{fontSize:11,color:C.text3}}>📍 {comp.location} · {comp.distance_km}km</div>
                      {comp.segments&&<div style={{fontSize:11,color:C.blue,marginTop:4}}>{comp.segments}</div>}
                    </div>
                  ))}
                </div>

                {/* AI Analysis results */}
                {competitorAnalysis && (
                  <div>
                    <div style={{fontSize:14,fontWeight:800,color:C.purple,marginBottom:14}}>🔍 AI Competitive Intelligence</div>
                    <div style={{background:C.surface,border:`1px solid ${C.purple}44`,borderRadius:12,padding:18,marginBottom:14}}>
                      <div style={{fontSize:13,color:C.text2,marginBottom:10,lineHeight:1.7}}>{competitorAnalysis.competitive_summary}</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        {competitorAnalysis.market_gaps?.map((gap,i)=>(
                          <div key={i} style={{padding:'8px 12px',background:C.accentBg,borderRadius:8,fontSize:12,color:C.accent}}>💡 {gap}</div>
                        ))}
                      </div>
                    </div>
                    <div style={{display:'grid',gap:10}}>
                      {competitorAnalysis.competitor_analysis?.map((comp,i)=>(
                        <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                            <div style={{fontSize:14,fontWeight:800,color:C.text}}>{comp.name}</div>
                            <span style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:700,background:comp.threat_level==='High'?C.redBg:comp.threat_level==='Medium'?C.amberBg:C.accentBg,color:comp.threat_level==='High'?C.red:comp.threat_level==='Medium'?C.amber:C.accent}}>
                              {comp.threat_level} Threat
                            </span>
                          </div>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10,fontSize:12}}>
                            <div style={{padding:'8px 12px',background:C.surface2,borderRadius:6}}>
                              <div style={{color:C.text3}}>Weekday Rate</div>
                              <div style={{fontWeight:700,color:C.text}}>{inr(comp.estimated_rate_weekday)}</div>
                            </div>
                            <div style={{padding:'8px 12px',background:C.surface2,borderRadius:6}}>
                              <div style={{color:C.text3}}>Weekend Rate</div>
                              <div style={{fontWeight:700,color:C.amber}}>{inr(comp.estimated_rate_weekend)}</div>
                            </div>
                          </div>
                          <div style={{fontSize:12,color:C.gold,marginBottom:6}}>✅ Botanix Advantage: {comp.botanix_advantage}</div>
                          {comp.weaknesses?.length>0&&<div style={{fontSize:11,color:C.red}}>⚠️ Their weakness: {comp.weaknesses[0]}</div>}
                        </div>
                      ))}
                    </div>
                    {competitorAnalysis.immediate_actions?.length>0&&(
                      <div style={{marginTop:14,background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:12,padding:16}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.gold,marginBottom:10}}>⚡ Immediate Actions</div>
                        {competitorAnalysis.immediate_actions.map((a,i)=>(
                          <div key={i} style={{display:'flex',gap:8,padding:'7px 0',borderBottom:`1px solid ${C.border}`,fontSize:12,color:C.text2}}>
                            <span style={{color:C.gold,fontWeight:700}}>{i+1}.</span>{a}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* OPPORTUNITIES */}
        {activeTab==='opportunities' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text}}>Revenue Opportunities</div>
              <button onClick={runMarketScan} disabled={scanning} style={{padding:'8px 16px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',opacity:scanning?.6:1}}>
                {scanning?'⏳':'🛰️'} Refresh
              </button>
            </div>
            {(dashboard?.opportunities||[]).length===0?(
              <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:32,marginBottom:10}}>💡</div>
                <div style={{fontSize:14,color:C.text2}}>Run market scan to detect opportunities</div>
              </div>
            ):(
              <div style={{display:'grid',gap:12}}>
                {(dashboard?.opportunities||[]).map((opp,i)=>(
                  <div key={i} style={{background:C.surface,border:`1px solid ${C.gold}33`,borderRadius:12,padding:20}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
                      <div>
                        <div style={{fontSize:16,fontWeight:800,color:C.text}}>{opp.title}</div>
                        <div style={{fontSize:11,color:C.text3,marginTop:2}}>{opp.opportunity_type}</div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize:22,fontWeight:900,color:C.gold}}>{inr(opp.revenue_opportunity)}</div>
                        <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:opp.priority==='High'?C.redBg:C.amberBg,color:opp.priority==='High'?C.red:C.amber}}>{opp.priority}</span>
                      </div>
                    </div>
                    <div style={{fontSize:13,color:C.text2,marginBottom:10}}>{opp.description}</div>
                    <div style={{fontSize:12,color:C.gold,marginBottom:12}}>💡 {opp.recommended_action}</div>
                    <button onClick={()=>generateCampaign(opp)} disabled={generatingCampaign}
                      style={{padding:'8px 18px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',opacity:generatingCampaign?.6:1}}>
                      {generatingCampaign?'⏳ Generating...':'📣 Generate Campaign'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CAMPAIGNS */}
        {activeTab==='campaigns' && (
          <div>
            <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:16}}>Campaign AI Generator</div>
            {!campaignBrief ? (
              <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:40,marginBottom:12}}>📣</div>
                <div style={{fontSize:14,color:C.text2,marginBottom:8}}>Select an opportunity to generate a campaign</div>
                <button onClick={()=>setActiveTab('opportunities')} style={{padding:'10px 20px',background:C.accentBg,color:C.accent,border:`1px solid ${C.accent}44`,borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                  View Opportunities →
                </button>
              </div>
            ) : (
              <div>
                {/* Campaign header */}
                <div style={{background:`linear-gradient(135deg,${C.primary||'#153D4B'},#1E5A70)`,borderRadius:14,padding:20,marginBottom:16}}>
                  <div style={{fontSize:20,fontWeight:900,color:C.gold,marginBottom:4}}>{campaignBrief.campaign_name}</div>
                  <div style={{fontSize:12,color:'rgba(255,255,255,.6)',marginBottom:12}}>{campaignBrief.campaign_type} · {campaignBrief.target_audience}</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                    {[
                      {label:'Budget',value:inr(campaignBrief.recommended_budget),color:C.gold},
                      {label:'Expected Leads',value:campaignBrief.expected_leads,color:C.blue},
                      {label:'Expected Bookings',value:campaignBrief.expected_bookings,color:C.accent},
                      {label:'Expected Revenue',value:inr(campaignBrief.expected_revenue),color:C.gold},
                    ].map((s,i)=>(
                      <div key={i} style={{textAlign:'center',padding:'10px',background:'rgba(255,255,255,.06)',borderRadius:8}}>
                        <div style={{fontSize:18,fontWeight:900,color:s.color}}>{s.value}</div>
                        <div style={{fontSize:9,color:'rgba(255,255,255,.5)',marginTop:2}}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Campaign content */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                    <div style={{fontSize:12,fontWeight:700,color:'#25D366',marginBottom:10}}>💬 WhatsApp Message</div>
                    <div style={{fontSize:13,color:C.text2,lineHeight:1.7,whiteSpace:'pre-wrap',background:C.surface2,padding:'12px',borderRadius:8}}>{campaignBrief.whatsapp_copy}</div>
                    <button onClick={()=>navigator.clipboard.writeText(campaignBrief.whatsapp_copy)} style={{marginTop:10,padding:'6px 14px',background:'rgba(37,211,102,.15)',color:'#25D366',border:'1px solid rgba(37,211,102,.3)',borderRadius:6,fontSize:11,cursor:'pointer'}}>Copy</button>
                  </div>

                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.blue,marginBottom:10}}>📧 Email</div>
                    <div style={{fontSize:12,fontWeight:700,color:C.text3,marginBottom:4}}>Subject: {campaignBrief.email_subject}</div>
                    <div style={{fontSize:13,color:C.text2,lineHeight:1.7,background:C.surface2,padding:'12px',borderRadius:8,maxHeight:200,overflowY:'auto'}}>{campaignBrief.email_copy}</div>
                  </div>

                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                    <div style={{fontSize:12,fontWeight:700,color:'#E1306C',marginBottom:10}}>📸 Instagram Caption</div>
                    <div style={{fontSize:13,color:C.text2,lineHeight:1.7,background:C.surface2,padding:'12px',borderRadius:8,whiteSpace:'pre-wrap'}}>{campaignBrief.instagram_caption}</div>
                    <button onClick={()=>navigator.clipboard.writeText(campaignBrief.instagram_caption)} style={{marginTop:10,padding:'6px 14px',background:'rgba(225,48,108,.15)',color:'#E1306C',border:'1px solid rgba(225,48,108,.3)',borderRadius:6,fontSize:11,cursor:'pointer'}}>Copy</button>
                  </div>

                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.gold,marginBottom:10}}>🎯 Campaign Details</div>
                    <div style={{fontSize:12,color:C.text2,lineHeight:1.8}}>
                      <div><strong style={{color:C.text}}>Offer:</strong> {campaignBrief.offer}</div>
                      <div><strong style={{color:C.text}}>Message:</strong> {campaignBrief.key_message}</div>
                      <div><strong style={{color:C.text}}>Channels:</strong> {campaignBrief.channels?.join(', ')}</div>
                      <div><strong style={{color:C.text}}>Dates:</strong> {campaignBrief.campaign_dates?.start} to {campaignBrief.campaign_dates?.end}</div>
                      <div><strong style={{color:C.text}}>Audience:</strong> {campaignBrief.audience_segments?.join(', ')}</div>
                    </div>
                    {campaignBrief.approval_required&&(
                      <div style={{marginTop:12,padding:'8px 12px',background:C.amberBg,borderRadius:6,fontSize:11,color:C.amber}}>⚠️ GM approval required before launch</div>
                    )}
                    <button style={{marginTop:12,width:'100%',padding:'10px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                      ✅ Approve & Launch Campaign
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* NCR EVENTS */}
        {activeTab==='ncrevents' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div>
                <div style={{fontSize:16,fontWeight:800,color:C.text}}>🎪 NCR Event Intelligence</div>
                <div style={{fontSize:12,color:C.text3,marginTop:2}}>Live venue data from Google Maps · AI-powered event & campaign recommendations</div>
              </div>
              <button onClick={loadEventIntelligence} disabled={loadingEvents}
                style={{padding:'10px 20px',background:loadingEvents?C.surface2:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:loadingEvents?C.text3:'#1A1A1A',border:'none',borderRadius:10,fontSize:13,fontWeight:700,cursor:loadingEvents?'default':'pointer'}}>
                {loadingEvents?'⏳ Analyzing NCR Events...':'🎪 Scan NCR Events'}
              </button>
            </div>

            {!eventIntel && !loadingEvents && (
              <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:14,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:48,marginBottom:16}}>🎪</div>
                <div style={{fontSize:16,fontWeight:700,color:C.text2,marginBottom:8}}>NCR Event Intelligence</div>
                <div style={{fontSize:13,color:C.text3,marginBottom:8}}>Scans Bharat Mandapam, Pragati Maidan, India Expo Mart</div>
                <div style={{fontSize:13,color:C.text3,marginBottom:24}}>Manesar, Aerocity, Gurgaon convention centres</div>
                <div style={{display:'flex',gap:10,justifyContent:'center',marginBottom:24,flexWrap:'wrap'}}>
                  {['🏛️ Bharat Mandapam','🏪 India Expo Mart','🏢 DLF Convention','✈️ Aerocity','🚗 Manesar Industrial'].map(v=>(
                    <span key={v} style={{padding:'6px 14px',background:C.surface2,border:`1px solid ${C.border}`,borderRadius:99,fontSize:12,color:C.text2}}>{v}</span>
                  ))}
                </div>
                <button onClick={loadEventIntelligence}
                  style={{padding:'14px 32px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:10,fontSize:15,fontWeight:800,cursor:'pointer'}}>
                  🎪 Scan NCR Events Now
                </button>
              </div>
            )}

            {loadingEvents && (
              <div style={{textAlign:'center',padding:60,color:C.text3}}>
                <div style={{fontSize:36,marginBottom:12}}>🔍</div>
                <div style={{fontSize:14,marginBottom:8}}>Scanning Google Maps for NCR venues...</div>
                <div style={{fontSize:12}}>Then analyzing events with AI...</div>
              </div>
            )}

            {eventIntel && (
              <div>
                {/* Monthly Outlook */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:20}}>
                  {Object.entries(eventIntel.monthly_outlook||{}).map(([month,data])=>(
                    <div key={month} style={{background:C.surface,border:`1px solid ${data.demand==='High'?C.red:data.demand==='Medium'?C.amber:C.accent}44`,borderRadius:12,padding:18,borderTop:`3px solid ${data.demand==='High'?C.red:data.demand==='Medium'?C.amber:C.accent}`}}>
                      <div style={{fontSize:15,fontWeight:800,color:C.text,textTransform:'capitalize',marginBottom:6}}>{month} 2026</div>
                      <div style={{fontSize:20,fontWeight:900,color:data.demand==='High'?C.red:data.demand==='Medium'?C.amber:C.accent,marginBottom:8}}>{data.demand}</div>
                      <div style={{fontSize:11,color:C.text3,marginBottom:8}}>{(data.key_events||[]).slice(0,2).join(' · ')}</div>
                      <div style={{fontSize:12,color:C.gold,fontWeight:600}}>{data.action}</div>
                    </div>
                  ))}
                </div>

                {/* Venue list from Google */}
                {venues.length>0&&(
                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,marginBottom:16}}>
                    <div style={{fontSize:13,fontWeight:800,color:C.blue,marginBottom:12}}>📍 Real NCR Venues (Google Maps)</div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                      {venues.map((v,i)=>(
                        <div key={i} style={{padding:'8px 14px',background:C.surface2,borderRadius:8,border:`1px solid ${C.border}`}}>
                          <div style={{fontSize:12,fontWeight:700,color:C.text}}>{v.name}</div>
                          <div style={{fontSize:10,color:C.text3}}>{v.distance_km}km · ⭐{v.rating||'N/A'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Events */}
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:14}}>
                    📅 Detected Events ({(eventIntel.events||[]).length})
                  </div>
                  <div style={{display:'grid',gap:10}}>
                    {(eventIntel.events||[]).map((event,i)=>(
                      <div key={i} style={{background:C.surface,border:`1px solid ${event.botanix_opportunity==='High'?C.red:event.botanix_opportunity==='Medium'?C.amber:C.accent}44`,borderRadius:12,padding:18,borderLeft:`4px solid ${event.botanix_opportunity==='High'?C.red:event.botanix_opportunity==='Medium'?C.amber:C.accent}`}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                          <div style={{flex:1}}>
                            <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:6,flexWrap:'wrap'}}>
                              <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:event.botanix_opportunity==='High'?C.redBg:event.botanix_opportunity==='Medium'?C.amberBg:C.accentBg,color:event.botanix_opportunity==='High'?C.red:event.botanix_opportunity==='Medium'?C.amber:C.accent}}>
                                {event.botanix_opportunity} Opportunity
                              </span>
                              <span style={{fontSize:11,color:C.text3}}>{event.category}</span>
                              <span style={{fontSize:11,color:C.text3}}>📍 {event.distance_from_botanix_km}km away</span>
                              <span style={{fontSize:11,color:C.text3,fontStyle:'italic'}}>{event.confidence} confidence</span>
                            </div>
                            <div style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:4}}>{event.event_name}</div>
                            <div style={{fontSize:12,color:C.text3,marginBottom:4}}>📍 {event.venue}</div>
                            <div style={{fontSize:12,color:C.amber,marginBottom:4}}>📅 {event.likely_dates}</div>
                            <div style={{fontSize:12,color:C.text2,marginBottom:4}}>👥 {event.audience} · {event.expected_attendance?.toLocaleString()} expected</div>
                            <div style={{fontSize:11,color:C.blue}}>📊 {event.demand_type}</div>
                          </div>
                          <div style={{textAlign:'right',flexShrink:0,marginLeft:16}}>
                            <div style={{fontSize:18,fontWeight:900,color:C.gold}}>{inr(event.revenue_opportunity)}</div>
                            <div style={{fontSize:10,color:C.text3}}>Revenue Opportunity</div>
                            <div style={{fontSize:11,color:C.text3,marginTop:4}}>~{event.estimated_room_demand} rooms</div>
                          </div>
                        </div>
                        <div style={{padding:'10px 14px',background:C.goldBg,borderRadius:8,fontSize:12,color:C.gold,borderLeft:`3px solid ${C.gold}`}}>
                          💡 <strong>Campaign:</strong> {event.recommended_campaign}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Marketing Strategy */}
                {eventIntel.marketing_strategy && (
                  <div style={{background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:12,padding:20,marginBottom:16}}>
                    <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:12}}>📣 AI Marketing Strategy</div>
                    <div style={{fontSize:13,color:C.text2,marginBottom:16,lineHeight:1.7}}>{eventIntel.marketing_strategy.summary}</div>

                    {/* Campaigns to launch */}
                    {eventIntel.marketing_strategy.campaigns_to_launch?.length>0&&(
                      <div style={{marginBottom:14}}>
                        <div style={{fontSize:12,fontWeight:700,color:C.text3,textTransform:'uppercase',marginBottom:10}}>Campaigns to Launch</div>
                        <div style={{display:'grid',gap:8}}>
                          {eventIntel.marketing_strategy.campaigns_to_launch.map((camp,i)=>(
                            <div key={i} style={{padding:'14px 16px',background:C.surface2,borderRadius:10,border:`1px solid ${C.border}`}}>
                              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                                <div style={{fontSize:14,fontWeight:700,color:C.text}}>{camp.campaign_name}</div>
                                <div style={{fontSize:13,fontWeight:800,color:C.gold}}>{inr(camp.expected_revenue)}</div>
                              </div>
                              <div style={{fontSize:11,color:C.amber,marginBottom:4}}>🎪 Trigger: {camp.trigger_event}</div>
                              <div style={{fontSize:11,color:C.text3,marginBottom:4}}>👥 {camp.target_audience} · 📅 {camp.timing}</div>
                              <div style={{fontSize:12,color:C.text2,marginBottom:6}}>{camp.message}</div>
                              <div style={{display:'flex',gap:6}}>
                                <span style={{fontSize:10,padding:'2px 8px',background:C.blueBg,color:C.blue,borderRadius:99}}>{camp.channel}</span>
                                <button onClick={()=>{
                                  setSelectedOpportunity({title:camp.campaign_name, description:camp.message, opportunity_type:camp.channel});
                                  setActiveTab('campaigns');
                                }} style={{padding:'2px 10px',background:C.goldBg,color:C.gold,border:`1px solid ${C.gold}44`,borderRadius:6,fontSize:10,fontWeight:700,cursor:'pointer'}}>
                                  Generate Full Campaign →
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pricing adjustments */}
                    {eventIntel.marketing_strategy.pricing_adjustments?.length>0&&(
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:C.text3,textTransform:'uppercase',marginBottom:10}}>Pricing Adjustments</div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:8}}>
                          {eventIntel.marketing_strategy.pricing_adjustments.map((adj,i)=>(
                            <div key={i} style={{padding:'12px 14px',background:C.surface2,borderRadius:8,border:`1px solid ${C.amber}33`}}>
                              <div style={{fontSize:12,color:C.amber,marginBottom:4}}>📅 {adj.dates}</div>
                              <div style={{fontSize:11,color:C.text3,marginBottom:6}}>{adj.reason}</div>
                              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                <span style={{fontSize:13,color:C.text2}}>{inr(adj.current_rate)}</span>
                                <span style={{fontSize:11,color:C.text3}}>→</span>
                                <span style={{fontSize:15,fontWeight:800,color:C.gold}}>{inr(adj.recommended_rate)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* LIVE INTELLIGENCE */}
        {activeTab==='live' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text}}>📡 Live Market Intelligence</div>
              <button onClick={loadLiveData} disabled={loadingLive}
                style={{padding:'9px 18px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',opacity:loadingLive?.6:1}}>
                {loadingLive?'⏳ Loading Live Data...':'📡 Fetch Live Data'}
              </button>
            </div>

            {!liveData && !loadingLive && (
              <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:40,marginBottom:12}}>📡</div>
                <div style={{fontSize:14,color:C.text2,marginBottom:8}}>Fetch live data from Weather, News & Google Maps</div>
                <div style={{fontSize:12,color:C.text3,marginBottom:20}}>Real competitor resorts · Live weather · Latest NCR news</div>
                <button onClick={loadLiveData}
                  style={{padding:'12px 28px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                  📡 Load Live Intelligence
                </button>
              </div>
            )}

            {liveData && (
              <div>
                {/* Market Pulse */}
                <div style={{background:`linear-gradient(135deg,#0D1A2A,#1A2E4A)`,borderRadius:14,padding:20,marginBottom:16,border:`1px solid ${C.gold}44`}}>
                  <div style={{fontSize:11,color:C.gold,textTransform:'uppercase',marginBottom:6}}>Live Market Pulse</div>
                  <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:10}}>{liveData.market_pulse}</div>
                  <div style={{fontSize:14,fontWeight:800,color:C.gold}}>Recommended Rate: {liveData.recommended_rate_adjustment}</div>
                </div>

                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
                  {/* Weather */}
                  {weather?.forecasts?.length>0 && (
                    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                      <div style={{fontSize:13,fontWeight:800,color:C.blue,marginBottom:12}}>🌤️ Gurgaon Weather Forecast</div>
                      {weather.forecasts.slice(0,4).map((f,i)=>(
                        <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:`1px solid ${C.border}`}}>
                          <div style={{fontSize:12,color:C.text3}}>{new Date(f.date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}</div>
                          <div style={{fontSize:12,color:C.text}}>{f.description}</div>
                          <div style={{display:'flex',gap:8,alignItems:'center'}}>
                            <span style={{fontSize:13,fontWeight:700,color:C.amber}}>{f.temp}°C</span>
                            {f.rain_probability>50&&<span style={{fontSize:11,color:C.blue}}>🌧️ {f.rain_probability}%</span>}
                          </div>
                        </div>
                      ))}
                      {liveData.weather_impact && (
                        <div style={{marginTop:10,padding:'8px 12px',background:C.amberBg,borderRadius:6,fontSize:12,color:C.amber}}>
                          {liveData.weather_impact.recommended_action}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Top 3 Actions */}
                  <div style={{background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:12,padding:18}}>
                    <div style={{fontSize:13,fontWeight:800,color:C.gold,marginBottom:12}}>⚡ Top 3 Actions Right Now</div>
                    {(liveData.top_3_actions||[]).map((action,i)=>(
                      <div key={i} style={{display:'flex',gap:10,padding:'10px 12px',background:C.surface2,borderRadius:8,marginBottom:8}}>
                        <span style={{color:C.gold,fontWeight:900,fontSize:16}}>{i+1}</span>
                        <span style={{fontSize:13,color:C.text2,lineHeight:1.5}}>{action}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Live Competitor Resorts */}
                {nearbyResorts.length>0 && (
                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,marginBottom:14}}>
                    <div style={{fontSize:13,fontWeight:800,color:C.purple,marginBottom:14}}>🗺️ Nearby Resorts (Google Maps — Live)</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:8}}>
                      {nearbyResorts.map((resort,i)=>(
                        <div key={i} style={{padding:'12px 14px',background:C.surface2,borderRadius:10,border:`1px solid ${C.border}`}}>
                          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>{resort.name}</div>
                          <div style={{fontSize:11,color:C.text3,marginBottom:6}}>{resort.address?.slice(0,40)}...</div>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                            <div style={{display:'flex',gap:4,alignItems:'center'}}>
                              <span style={{color:C.amber,fontSize:12}}>⭐</span>
                              <span style={{fontSize:12,fontWeight:700,color:C.text}}>{resort.rating||'N/A'}</span>
                              <span style={{fontSize:10,color:C.text3}}>({resort.reviews||0})</span>
                            </div>
                            <span style={{fontSize:10,padding:'2px 6px',borderRadius:99,background:resort.open_now?C.accentBg:C.redBg,color:resort.open_now?C.accent:C.red}}>
                              {resort.open_now?'Open':'Closed'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {liveData.competitor_intelligence && (
                      <div style={{marginTop:12,padding:'12px 14px',background:C.purpleBg,borderRadius:8,fontSize:12,color:C.purple}}>
                        🔍 {liveData.competitor_intelligence.market_position}
                      </div>
                    )}
                  </div>
                )}

                {/* Live News */}
                {news?.articles?.length>0 && (
                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,marginBottom:14}}>
                    <div style={{fontSize:13,fontWeight:800,color:C.cyan||C.blue,marginBottom:12}}>📰 Latest NCR News</div>
                    {news.articles.slice(0,4).map((article,i)=>(
                      <div key={i} style={{padding:'10px 0',borderBottom:`1px solid ${C.border}`}}>
                        <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:4}}>{article.title}</div>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <span style={{fontSize:10,color:C.text3}}>{article.source} · {new Date(article.published).toLocaleDateString('en-IN')}</span>
                          <span style={{fontSize:10,padding:'2px 8px',borderRadius:99,background:article.relevance==='High'?C.redBg:C.amberBg,color:article.relevance==='High'?C.red:C.amber}}>{article.relevance}</span>
                        </div>
                      </div>
                    ))}
                    {liveData.news_insights?.length>0 && (
                      <div style={{marginTop:12}}>
                        {liveData.news_insights.slice(0,2).map((insight,i)=>(
                          <div key={i} style={{padding:'8px 12px',background:C.accentBg,borderRadius:6,marginBottom:6,fontSize:12,color:C.accent}}>
                            💡 {insight.action}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Live Opportunities */}
                {liveData.live_opportunities?.length>0 && (
                  <div style={{background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:12,padding:18}}>
                    <div style={{fontSize:13,fontWeight:800,color:C.gold,marginBottom:12}}>💡 Live Opportunities Detected</div>
                    {liveData.live_opportunities.map((opp,i)=>(
                      <div key={i} style={{padding:'12px 14px',background:C.surface2,borderRadius:8,marginBottom:8,borderLeft:`3px solid ${opp.urgency==='High'?C.red:opp.urgency==='Medium'?C.amber:C.accent}`}}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{opp.title}</div>
                          <span style={{fontSize:10,color:C.text3}}>{opp.source}</span>
                        </div>
                        <div style={{fontSize:12,color:C.text2,marginBottom:6}}>{opp.description}</div>
                        <div style={{fontSize:12,color:C.gold}}>→ {opp.action}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ASK B-MINT */}
        {activeTab==='ask' && (
          <div>
            <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:16}}>Ask B-MINT Market Intelligence AI</div>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,marginBottom:12,overflow:'hidden'}}>
              <div style={{maxHeight:450,overflowY:'auto',padding:20}}>
                {chatMessages.length===0 && (
                  <div style={{textAlign:'center',padding:40,color:C.text3}}>
                    <div style={{fontSize:40,marginBottom:12}}>🌐</div>
                    <div style={{fontSize:14,color:C.text2,marginBottom:16}}>Ask B-MINT about market intelligence</div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center'}}>
                      {[
                        'What should we be worried about next month?',
                        'Which weekends should we increase prices?',
                        'What are competitors promoting right now?',
                        'Why might October be high demand?',
                        'What campaign should we run for Diwali?',
                        'Which upcoming events can generate corporate business?',
                      ].map(q=>(
                        <button key={q} onClick={()=>setChatInput(q)} style={{padding:'6px 12px',background:C.surface2,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:11,cursor:'pointer'}}>{q}</button>
                      ))}
                    </div>
                  </div>
                )}
                {chatMessages.map((m,i)=>(
                  <div key={i} style={{marginBottom:14,display:'flex',flexDirection:m.role==='user'?'row-reverse':'row',gap:8}}>
                    <div style={{width:32,height:32,borderRadius:'50%',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,
                      background:m.role==='user'?'#153D4B':`linear-gradient(135deg,${C.gold},#E8C97A)`}}>
                      {m.role==='user'?'👤':'🌐'}
                    </div>
                    <div style={{maxWidth:'80%',padding:'12px 16px',borderRadius:12,background:m.role==='user'?'#153D4B':C.surface2,border:`1px solid ${C.border}`}}>
                      <div style={{fontSize:13,lineHeight:1.8,color:C.text,whiteSpace:'pre-wrap'}}>{m.content}</div>
                    </div>
                  </div>
                ))}
                {chatLoading&&<div style={{textAlign:'center',padding:10,color:C.text3}}>⏳ B-MINT analyzing...</div>}
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <input value={chatInput} onChange={e=>setChatInput(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&askBMint()}
                placeholder="Ask about market conditions, events, competitors, pricing strategy..."
                style={{flex:1,padding:'12px 16px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:10,color:C.text,fontSize:13,outline:'none'}} />
              <button onClick={askBMint} disabled={chatLoading||!chatInput.trim()}
                style={{padding:'12px 20px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',opacity:chatLoading||!chatInput.trim()?.5:1}}>
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
